import React from "react";
import {
  currencyFormatter,
  formatOrderStatus,
  getUptoFourteenDaysLater,
} from "../../utils/formatting";
import { useNavigate } from "react-router-dom";
import { orderStatus } from "../../constants/commonConstants.js";
import Pagination from "./pagination.jsx";
import { ClipLoader } from "react-spinners";
import { formatYYYY_MM_DD, formatYYYY_MM_DD_HH_MM_A } from "../../utils/dateFormating.js";
import { Trash2 } from "lucide-react";

const Card = ({
  buttonType,
  orderStatusFilter,
  onViewOrder,
  onSecondaryViewOrder,
  secondaryButtonText,
  onDeleteDraft,
  currentOrders,
  loading,
  className,
  tableName,
}) => {
  const navigate = useNavigate();

  // Function to determine the circle color based on order status
  const getStatusCircleColor = (status) => {
    if (
      ["PENDING_PAYMENT", "IN_REVISION", "COMPLAINT_RAISED"].includes(status)
    ) {
      return "bg-light-red";
    } else if (status === "ORDER_IN_PROGRESS") {
      return "bg-[#06BCCC]";
    } else if (["ORDER_DELIVERED", "COMPLETED", "RESOLVED"].includes(status)) {
      return "bg-blue-shades";
    } else {
      return "bg-gray-500"; // Default color if no status matches
    }
  };

  const getOrderStatusText = (status) => {
    if (
      status === orderStatus.PENDING_PAYMENT &&
      (tableName === orderStatus.PENDING_PAYMENT || tableName === "MY_ORDERS")
    ) {
      return "Draft";
    }
    return formatOrderStatus(orderStatus[status]) || status;
  };

  // Handle the button click for different actions
  const handleButtonClick = (orderId, collectionId, objectId) => {
      // Navigate to the order page
      onViewOrder(orderId, collectionId, objectId);
  };

  const isButtonDisabled = (completedDate, status) => {
    if (tableName === orderStatus.COMPLAINT_RAISED) {
      // const completedDate = completedDate;
      if (!completedDate) return true; // Disable if no deadline
      const fourteenDaysLater = getUptoFourteenDaysLater(completedDate);
      const currentDate = new Date();

      return (
        status === orderStatus.RESOLVED || 
        status === orderStatus.COMPLAINT_RAISED || 
        (status === orderStatus.COMPLETED && fourteenDaysLater < currentDate)
      );
    }
  };

  return (
    <div className=" relative font-inter flex flex-col min-h-[400px] justify-center pb-4">
      {/* Show loading spinner if data is still loading */}
      {loading ? (
        <div className="flex justify-center">
          <ClipLoader color="#60a5fa" loading={loading} size={44} speedMultiplier={0.5}/>
        </div>
      ) : (
        <>
          {currentOrders?.map((order) => (
            <div
              key={order.id}
              className="border border-[#D7D7E0] mb-4 rounded-[24px] transition-shadow duration-200 ease-in-out"
            >
              <div className="bg-soft-gray px-6 py-3 rounded-t-[24px]">
                <h3 className="font-semibold text-[15px] text-primary leading-5 mb-[10px]">
                  {order?.id} - {order?.orderRequirements?.subject}
                </h3>
                <div className="flex justify-between h-[20px] items-center">
                  <div className="flex">
                    {/* Circle with color based on order status */}
                    <span
                      className={`rounded-full w-3 h-3 relative top-[0px] mr-1 ${getStatusCircleColor(
                        order?.status
                      )}`}
                    ></span>
                    <p className="text-gray-700 text-level-5 leading-3 font-medium">
                      {getOrderStatusText(order?.status)}
                    </p>
                  </div>
                  <h3 className="font-semibold text-[15px] text-secondary leading-5 max-460-br:translate-y-3">
                    {currencyFormatter.format(order?.price || 0) || "$ 00.00"}
                  </h3>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-between p-6">
                <div className="max-460-br:mb-3">
                  <div className="flex flex-row justify-between leading-[20px] text-[15px]">
                    <p className="text-secondary text-[15px] mr-[10px] font-semibold">
                      Created
                    </p>
                    <p className="text-gray text-[15px] font-normal">
                      {formatYYYY_MM_DD(order?.createdAt || '') || '' }
                    </p>
                  </div>
                  <div className="flex flex-row justify-between leading-[20px] text-[15px]">
                    <p className="text-secondary font-semibold mr-3">
                      {/* {Due : Completed} */}
                      {
                        tableName === "ALL" ||
                        order?.status === orderStatus.ORDER_IN_PROGRESS || 
                        order?.status === orderStatus.ORDER_DELIVERED || 
                        order?.status === orderStatus.PENDING_WRITER || 
                        order?.status === orderStatus.IN_REVISION ||
                        order?.status === orderStatus.PENDING_PAYMENT ? 
                        "Due" :
                        "Completed"
                      }
                    </p>
                    <p className="text-gray font-normal ">
                      {
                        tableName === "ALL" ||
                        order?.status === orderStatus.ORDER_IN_PROGRESS || 
                        order?.status === orderStatus.ORDER_DELIVERED || 
                        order?.status === orderStatus.PENDING_WRITER || 
                        order?.status === orderStatus.IN_REVISION ||
                        order?.status === orderStatus.PENDING_PAYMENT ? 
                        formatYYYY_MM_DD_HH_MM_A(order?.orderRequirements?.deadline || '') || '' 
                        :formatYYYY_MM_DD(order?.completedDate || '') || ''
                      }
                    </p>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {secondaryButtonText && onSecondaryViewOrder && (
                    <button
                      className="flex items-center justify-center rounded-[5px] bg-light-blue px-4 py-2 font-roboto text-[13px] font-semibold leading-4 text-blue-shades transition-colors"
                      onClick={() => onSecondaryViewOrder(order.id, order?.threadId, order?._id)}
                    >
                      {secondaryButtonText}
                    </button>
                  )}
                  <button
                    disabled={
                      order?.status === orderStatus.COMPLETED ||
                      order?.status === orderStatus.COMPLAINT_RAISED ||
                      order?.status === orderStatus.RESOLVED ?
                      isButtonDisabled (
                        order?.completedDate,
                        order?.status
                      ) : 
                      false
                    }
                    className={`px-4 py-2 flex items-center justify-center rounded-[5px] text-[13px] transition-colors leading-4 font-semibold font-roboto
                      ${
                        order?.status === orderStatus.COMPLETED ||
                        order?.status === orderStatus.COMPLAINT_RAISED ||
                        order?.status === orderStatus.RESOLVED ?
                        isButtonDisabled(
                          order?.completedDate,
                          order?.status
                        )
                          ? "bg-slate-300 text-slate-600 opacity-40"
                          : "bg-light-blue text-blue-shades opacity-100"
                          : "bg-light-blue text-blue-shades opacity-100"
                      }
                      `}
                    onClick={() => handleButtonClick(order.id, order?.threadId, order?._id)} // Handle button click to navigate
                  >
                    {
                      order?.status === orderStatus.RESOLVED && tableName === orderStatus.COMPLAINT_RAISED ? 
                      formatOrderStatus(orderStatus.RESOLVED): 
                      order?.status === orderStatus.COMPLAINT_RAISED && tableName === orderStatus.COMPLAINT_RAISED?
                      "Already Raised" : 
                      buttonType || "View Order"}
                  </button>
                  {tableName === orderStatus.PENDING_PAYMENT && onDeleteDraft && (
                    <button
                      type="button"
                      onClick={() => onDeleteDraft(order)}
                      className="flex h-8 w-8 items-center justify-center rounded-[5px] border border-red-100 bg-red-50 text-red-600 transition-colors hover:bg-red-100"
                      aria-label={`Delete draft ${order?.id || ""}`}
                      title="Delete draft"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default Card;
